import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';

// Axios instance for API
const api = axios.create({
  baseURL: 'https://app.writeapp.in',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokenExpiry, setTokenExpiry] = useState(null);
  const [lastTokenFetch, setLastTokenFetch] = useState(null); // Track when token was last fetched

  const refreshingRef = useRef(false);
  const refreshTimeoutRef = useRef(null);

  // Constants for token management
  const TOKEN_REFRESH_INTERVAL = 20 * 60 * 60 * 1000; // 20 hours (refresh every 20 hours)
  const TOKEN_REFRESH_BUFFER = 2 * 60 * 60 * 1000; // 2 hours before expiry

  // Helper function to get user ID from user object
  const getUserId = (userObj = user) => {
    return userObj?.id || userObj?.user_id || userObj?.userId;
  };

  // Schedule automatic token refresh
  const scheduleTokenRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    if (!user || !lastTokenFetch) return;

    const now = Date.now();
    const timeSinceLastFetch = now - lastTokenFetch;
    let refreshInterval = TOKEN_REFRESH_INTERVAL - timeSinceLastFetch;

    // If we have token expiry info, use it for more precise timing
    if (tokenExpiry) {
      const expiryTime = new Date(tokenExpiry).getTime();
      const timeUntilExpiry = expiryTime - now - TOKEN_REFRESH_BUFFER;
      refreshInterval = Math.min(refreshInterval, timeUntilExpiry);
    }

    // Ensure we don't schedule negative or very small intervals
    refreshInterval = Math.max(1000, refreshInterval); // At least 1 second

    refreshTimeoutRef.current = setTimeout(async () => {
      if (user && !refreshingRef.current) {
        console.log('Auto-refreshing token...');
        const result = await refreshToken();
        if (result.success) {
          scheduleTokenRefresh(); // Schedule next refresh
        } else {
          console.error('Auto token refresh failed:', result.message);
          // Optionally handle failed refresh (logout user, show notification, etc.)
        }
      }
    }, refreshInterval);

    console.log(`Token refresh scheduled in ${Math.round(refreshInterval / 1000 / 60)} minutes`);
  }, [user, tokenExpiry, lastTokenFetch]);

  // Refresh token function
  const refreshToken = useCallback(async () => {
    if (refreshingRef.current) {
      return { success: false, message: 'Refresh already in progress' };
    }

    refreshingRef.current = true;

    try {
      const res = await api.post('/auth/refresh');
      if (res.status === 200) {
        const now = Date.now();
        setLastTokenFetch(now); // Update last token fetch time

        // Update user data if returned
        if (res.data && (res.data.id || res.data.user_id || res.data.email)) {
          setUser(res.data);
        }

        // Update token expiry if provided
        if (res.data.expires_at || res.data.token_expiry) {
          setTokenExpiry(res.data.expires_at || res.data.token_expiry);
        }

        return { success: true };
      } else {
        return { success: false, message: 'Token refresh failed' };
      }
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.error || 'Token refresh failed';
      console.error('Token refresh error:', message);

      // If refresh fails, clear user and token data
      setUser(null);
      setTokenExpiry(null);
      setLastTokenFetch(null);

      return { success: false, message };
    } finally {
      refreshingRef.current = false;
    }
  }, []);

  // Create authenticated API instance with interceptors
  const getAuthenticatedApi = useCallback(() => {
    const authenticatedApi = axios.create({
      baseURL: 'https://app.writeapp.in',
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to ensure fresh tokens
    authenticatedApi.interceptors.request.use(
      async (config) => {
        // Check if token is about to expire and refresh preemptively
        if (tokenExpiry && user && !refreshingRef.current) {
          const now = Date.now();
          const expiryTime = new Date(tokenExpiry).getTime();
          const timeUntilExpiry = expiryTime - now;

          if (timeUntilExpiry < TOKEN_REFRESH_BUFFER) {
            console.log('Token expiring soon, refreshing preemptively...');
            await refreshToken();
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle 401 errors
    authenticatedApi.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          const refreshResult = await refreshToken();

          if (refreshResult.success) {
            return authenticatedApi(originalRequest);
          } else {
            setUser(null);
            setTokenExpiry(null);
            setLastTokenFetch(null);
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }
    );

    return authenticatedApi;
  }, [refreshToken, tokenExpiry, user]);

  // Fetch user on mount
  const fetchCurrentUser = async () => {
    try {
      const res = await api.get('/auth/me');
      const now = Date.now();

      setUser(res.data);
      setLastTokenFetch(now); // Set last token fetch time

      // Extract token expiry if available
      if (res.data.expires_at || res.data.token_expiry) {
        setTokenExpiry(res.data.expires_at || res.data.token_expiry);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        const refreshResult = await refreshToken();
        if (refreshResult.success) {
          try {
            const res = await api.get('/auth/me');
            const now = Date.now();

            setUser(res.data);
            setLastTokenFetch(now); // Set last token fetch time

            if (res.data.expires_at || res.data.token_expiry) {
              setTokenExpiry(res.data.expires_at || res.data.token_expiry);
            }
            return;
          } catch (retryErr) {
            setUser(null);
            setTokenExpiry(null);
            setLastTokenFetch(null);
          }
        } else {
          setUser(null);
          setTokenExpiry(null);
          setLastTokenFetch(null);
        }
      } else {
        setUser(null);
        setTokenExpiry(null);
        setLastTokenFetch(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // Setup automatic token refresh when user logs in
  useEffect(() => {
    if (user && lastTokenFetch) {
      scheduleTokenRefresh();
    } else {
      // Clear refresh timer when user logs out
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [user, lastTokenFetch, scheduleTokenRefresh]);

  // Initial user fetch
  useEffect(() => {
    fetchCurrentUser();
  }, []);

  const login = async ({ email, password }) => {
    try {
      const res = await api.post('/auth/login', { email, password });
      if (res.status === 200) {
        const now = Date.now();

        setUser(res.data);
        setLastTokenFetch(now);

        if (res.data.expires_at || res.data.token_expiry) {
          setTokenExpiry(res.data.expires_at || res.data.token_expiry);
        }

        // ✅ NEW: Schedule auto-refresh immediately after login
        scheduleTokenRefresh();

        return { success: true };
      } else {
        return { success: false, message: res.data.message || 'Login failed.' };
      }
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.error || 'Login failed.';
      return { success: false, message };
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      console.error('Logout request failed:', err);
    } finally {
      // Always clear local state regardless of API response
      setUser(null);
      setTokenExpiry(null);
      setLastTokenFetch(null);
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    }
    return { success: true };
  };

  const register = async (formData) => {
    try {
      const res = await api.post('/auth/register', formData);
      if (res.status === 200) {
        const now = Date.now();

        if (res.data.id || res.data.user_id) {
          setUser(res.data);
          setLastTokenFetch(now); // Set last token fetch time on registration

          if (res.data.expires_at || res.data.token_expiry) {
            setTokenExpiry(res.data.expires_at || res.data.token_expiry);
          }
        } else {
          await fetchCurrentUser();
        }
        return { success: true };
      } else {
        return { success: false, message: res.data.message || 'Registration failed.' };
      }
    } catch (err) {
      const data = err.response?.data;
      let errorMessage = 'Registration failed.';
      if (typeof data === 'string') {
        errorMessage = data;
      } else if (data?.detail || data?.error) {
        errorMessage = data.detail || data.error;
      } else if (typeof data === 'object') {
        const combined = Object.entries(data)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
          .join('; ');
        errorMessage = combined || errorMessage;
      }
      return { success: false, message: errorMessage };
    }
  };

  const changePassword = async (formData) => {
    try {
      await api.post('/auth/change-password', formData);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.error || 'Password change failed.';
      return { success: false, message };
    }
  };

  // Permission helper functions
  const canModifyComment = (comment) => {
    if (!user) return false;
    const currentUserId = getUserId();
    const commentUserId = comment?.user_id || comment?.user?.id;
    return currentUserId === commentUserId || user.role === 'moderator';
  };

  const canModifyInterpretation = (interpretation) => {
    if (!user) return false;
    const currentUserId = getUserId();
    const interpretationUserId = interpretation?.user_id || interpretation?.user?.id;
    return currentUserId === interpretationUserId || user.role === 'moderator';
  };

  const canModifyPost = (post) => {
    if (!user) return false;
    const currentUserId = getUserId();
    const postUserId = post?.user_id || post?.author?.id;
    return currentUserId === postUserId || user.role === 'moderator';
  };

  const canModifyBlog = (blog) => {
    if (!user) return false;
    const currentUserId = getUserId();
    const blogUserId = blog?.user_id || blog?.author?.id;
    return currentUserId === blogUserId || user.role === 'moderator';
  };

  const isModerator = () => {
    return user?.role === 'moderator';
  };

  const isOwner = (itemUserId) => {
    const currentUserId = getUserId();
    return currentUserId && itemUserId &&
      (String(currentUserId) === String(itemUserId) || currentUserId === itemUserId);
  };

  // API functions for CRUD operations
  const deleteComment = async (commentId) => {
    try {
      const authenticatedApi = getAuthenticatedApi();
      await authenticatedApi.delete(`/comments/${commentId}`);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.error || 'Failed to delete comment.';
      return { success: false, message };
    }
  };

  const updateComment = async (commentId, content) => {
    try {
      const authenticatedApi = getAuthenticatedApi();
      const res = await authenticatedApi.put(`/comments/${commentId}`, { content });
      return { success: true, data: res.data };
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.error || 'Failed to update comment.';
      return { success: false, message };
    }
  };

  const deleteInterpretation = async (interpretationId) => {
    try {
      const authenticatedApi = getAuthenticatedApi();
      await authenticatedApi.delete(`/interpretations/${interpretationId}`);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.error || 'Failed to delete interpretation.';
      return { success: false, message };
    }
  };

  const updateInterpretation = async (interpretationId, data) => {
    try {
      const authenticatedApi = getAuthenticatedApi();
      const res = await authenticatedApi.put(`/interpretations/${interpretationId}`, data);
      return { success: true, data: res.data };
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.error || 'Failed to update interpretation.';
      return { success: false, message };
    }
  };

  const deletePost = async (postId) => {
    try {
      const authenticatedApi = getAuthenticatedApi();
      await authenticatedApi.delete(`/post/${postId}`);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.error || 'Failed to delete post.';
      return { success: false, message };
    }
  };

  const updatePost = async (postId, data) => {
    try {
      const authenticatedApi = getAuthenticatedApi();
      const res = await authenticatedApi.put(`/post/${postId}`, data);
      return { success: true, data: res.data };
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.error || 'Failed to update post.';
      return { success: false, message };
    }
  };

  const deleteBlog = async (blogId) => {
    try {
      const authenticatedApi = getAuthenticatedApi();
      await authenticatedApi.delete(`/blog/${blogId}`);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.error || 'Failed to delete blog.';
      return { success: false, message };
    }
  };

  const updateBlog = async (blogId, data) => {
    try {
      const authenticatedApi = getAuthenticatedApi();
      const res = await authenticatedApi.put(`/blog/${blogId}`, data);
      return { success: true, data: res.data };
    } catch (err) {
      const message = err.response?.data?.detail || err.response?.data?.error || 'Failed to update blog.';
      return { success: false, message };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        // CORE AUTH
        user,
        loading,
        isAuthenticated: !!user,
        login,
        logout,
        register,
        changePassword,
        fetchCurrentUser,
        refreshToken,
        getAuthenticatedApi,
        tokenExpiry,
        lastTokenFetch,

        // USER PROPERTIES
        userId: getUserId(),
        userRole: user?.role,
        userEmail: user?.email,
        userName: user?.name,

        // PERMISSION HELPERS
        canModifyComment,
        canModifyInterpretation,
        canModifyPost,
        canModifyBlog,
        isModerator,
        isOwner,

        // CRUD OPERATIONS
        deleteComment,
        updateComment,
        deleteInterpretation,
        updateInterpretation,
        deletePost,
        updatePost,
        deleteBlog,
        updateBlog,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;